import { Table, checkoutRedirectUrl, useApiQuery, type Column, type Invoice } from 'grabbitbot-legacy-ui'
import { paymentClient } from '../../api'
import { formatNumberN } from './paymentFlow'
import { DateTime, PaymentPage } from './paymentShared'
import { failureText } from '../../api/errorMessages'

/** Only an absolute https link to an invoice is rendered (same rule as the
 * Checkout redirect). */
function ExternalLink({ href, children }: { href: string | null; children: string }) {
  const safe = checkoutRedirectUrl(href)
  if (!safe) return null
  return (
    <a className="portal-button-link" href={safe} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  )
}

/**
 * `/payment/invoices` — legacy `PaymentInvoiceListComponent`: the Stripe
 * invoices (`GET /payment/invoices`, the search envelope; rows in `.data`)
 * with links to Stripe's hosted page / PDF.
 *
 * Deliberate difference (documented, changelog 0.7.0): legacy divided
 * `total` by 100 a SECOND time — the backend export already converts
 * Stripe's cents (`fs_models.invoice_export`), so legacy showed 0.20 for a
 * 19.99 invoice. The total is shown as exported.
 */
export function PaymentInvoicesPage() {
  const { data, loading, error } = useApiQuery(() => paymentClient.searchInvoices(), [])
  const rows = data && Array.isArray(data.data) ? data.data : []
  const columns: Column<Invoice>[] = [
    { key: 'number', label: 'Number', align: 'left', sortValue: (r) => r.number ?? '' },
    { key: 'effective_at', label: 'Date', align: 'left', sortValue: (r) => r.effective_at ?? '', format: (_v, r) => <DateTime value={r.effective_at} /> },
    {
      key: 'total',
      label: 'Total',
      align: 'right',
      numeric: true,
      sortValue: (r) => r.total ?? 0,
      format: (_v, r) => `${(r.currency ?? '').toUpperCase()} ${formatNumberN(r.total ?? 0, 2)}`.trim(),
    },
    {
      key: 'links',
      label: '',
      align: 'right',
      sortable: false,
      format: (_v, r) => (
        <span className="portal-inline-actions">
          <ExternalLink href={r.hosted_invoice_url}>View on Stripe</ExternalLink>
          <ExternalLink href={r.invoice_pdf}>Download PDF from Stripe</ExternalLink>
        </span>
      ),
    },
  ]
  return (
    <PaymentPage title="Invoices">
      {loading && <p>Loading invoices…</p>}
      {error && (
        <p className="portal-notice portal-notice--error" role="alert">
          {failureText(error, 'Could not load invoices')}
        </p>
      )}
      {!loading && !error && rows.length === 0 && <p className="portal-text-muted">No invoices.</p>}
      {rows.length > 0 && (
        <section className="portal-card">
          <div className="portal-table-scroll">
            <Table columns={columns} rows={rows} rowKey={(r, i) => r.id ?? i} initialSort={{ key: 'effective_at', dir: 'desc' }} />
          </div>
        </section>
      )}
    </PaymentPage>
  )
}
